import axios from "axios";

/**
 * A simple flag that other parts of the app can check to see whether a
 * 401-triggered redirect is already in progress.  While this is `true`,
 * error toasts should be suppressed so the user doesn't see a flash of
 * "Request failed with status code 401" before the login page appears.
 */
export let isSessionExpiredRedirectInProgress = false;

/** Call this when the user successfully logs back in so that future
 *  401 responses are not suppressed. */
export const resetSessionExpiredFlag = (): void => {
	isSessionExpiredRedirectInProgress = false;
};

/**
 * Installs a **global** Axios response interceptor on the default
 * `axios` instance.  This catches 401 responses from every call in the
 * app – including slices that bypass `ApiProvider` and use `axios`
 * directly (e.g. `eventFilterSlice`).
 *
 * When a 401 is detected the interceptor:
 *   1. Sets the `isSessionExpiredRedirectInProgress` flag so that UI
 *      error handlers (toasts, etc.) can suppress the message.
 *   2. Dispatches the same `auth:session-expired` custom event that
 *      `AuthProvider` listens for, which triggers `logout()` →
 *      `saveReturnUrl()` → redirect to `/login`.
 *   3. Still rejects the promise so that calling code doesn't proceed
 *      as if the request succeeded.
 */
export const setupGlobalAxiosInterceptor = (): void => {
	axios.interceptors.response.use(
		(response) => response,
		(error) => {
			if (error?.response?.status === 401 && !isSessionExpiredRedirectInProgress) {
				isSessionExpiredRedirectInProgress = true;
				window.dispatchEvent(new CustomEvent("auth:session-expired"));
			}
			return Promise.reject(error);
		},
	);
};
